import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import {
  type MultiplesGeneratorAdditionalCommandContext,
  type MultiplesGeneratorModel,
  type MultiplesGeneratorOuterState,
  type MultiplesGeneratorState,
  multiplesGeneratorInitialState,
} from 'pages/ComponentGallery/gallerySections/MultiplesGenerator/model/MultiplesGeneratorModel';
import { useCommandModel } from 'utils/hooks/useCommandModel';

/**
 * Just some function to demonstrate external api provided to model
 *
 * In this case we could just have `const generateRandomNumber = () => Math.floor(Math.random() * 100000)`
 * and provide that directly to command model context.
 * The hook is there to demonstrate how easily can we use hooks as command model context dependencies.
 */
const useRandomGenerator = () =>
  useStableCallback(() => Math.floor(Math.random() * 100000));

/**
 * The hook to instantiate given command model with all dependencies
 *
 * This is the "orchestration" component of your model. See useCommandModel doc
 * to understand what is the whole thing good for.
 *
 * You can add extra code here to handle initialization / lifecycle / extra fields
 * here, but it is strongly advised to keep this hook simple. Orchestration
 * components shouldn't contain implementation details of specific features,
 * since this way you can build a spaghetto faster than you think. If you really need
 * some more complex feature directly here, try to extract it to a separate hook or so
 * and load just the hook here.
 *
 * Note: you can extend the model with some extra fields that don't fit to state logic,
 * for example element refs that you need to share between subcomponents. It can be done this way:
 * ```
 * export const useExtraGenerator = () => {
 *   const model = useCommandModel(...);
 *   const someRef = useRef(null);
 *   return useMemo(
 *     () => ({
 *       ...model,
 *       someRef,
 *     }),
 *     []
 *   );
 * }
 * ```
 */
export const useMultiplesGenerator = (
  baseNumber: number,
): MultiplesGeneratorModel =>
  useCommandModel<
    // these generics can be inferred, they are explicitly there just for explanatory purposes of the demo
    MultiplesGeneratorState,
    MultiplesGeneratorAdditionalCommandContext,
    MultiplesGeneratorOuterState
  >(
    // initial state supports also lazy initialization - you can provide S | () => S here.
    multiplesGeneratorInitialState,
    // Put dependencies of your commands like api calling functions, references to other models
    // etc. here. These dependencies are visible only in commands via cmdContext.
    //
    // Important: you don't need to memoize this whole object, but individual fields should
    // have mostly stable identity.
    // See useCommandModel's additionalCommandContext argument docs for details.
    {
      generateRandomNumber: useRandomGenerator(),
    },
    // "State" (potentially regularly updated) fields that are managed by e.g. parent components
    // can go here. They will be available both in hook result and in cmdContext.getState().
    // See useCommandModel's outerState argument docs for details.
    {
      baseNumber,
    },
  );
